import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import type {components} from '@/lib/api-schema'
import {usePagedList} from '@/hooks/common/use-paged-list'

export type JefeInmediato = components['schemas']['JefeInmediatoRead']
export type MiArea = components['schemas']['MiAreaRead']
export type MiAreaPersona = components['schemas']['MiAreaPersonRead']
export type AreaResponsable = components['schemas']['AreaResponsableRead']

// Operador-gated on the backend.
export function useJefeInmediato(businessId: string | undefined, employeeId: string | undefined) {
  return useQuery({
    queryKey: ['jefe-inmediato', businessId, employeeId],
    queryFn: async () =>
      (await api.get<JefeInmediato>(`/businesses/${businessId}/employees/${employeeId}/jefe-inmediato`)).data,
    enabled: !!businessId && !!employeeId,
  })
}

export function useSetJefeDirecto(businessId: string, employeeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (jefeDirectoEmployeeId: string | null) =>
      (
        await api.patch(`/businesses/${businessId}/employees/${employeeId}/jefe-directo`, {
          jefe_directo_employee_id: jefeDirectoEmployeeId,
        })
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['employees', businessId, employeeId]})
      queryClient.invalidateQueries({queryKey: ['jefe-inmediato', businessId, employeeId]})
    },
  })
}

// Also invalidates capabilities: a member may have just become responsable de área.
export function useSetAreaResponsable(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({areaId, responsableEmployeeId}: {areaId: string; responsableEmployeeId: string | null}) =>
      (
        await api.put<AreaResponsable>(`/businesses/${businessId}/areas/${areaId}/responsable`, {
          responsable_employee_id: responsableEmployeeId,
        })
      ).data,
    onSuccess: () => {
      // useTags reads areas under ['tags', businessId, 'areas'], not ['areas', businessId].
      queryClient.invalidateQueries({queryKey: ['tags', businessId, 'areas']})
      queryClient.invalidateQueries({queryKey: ['employees', businessId]})
      queryClient.invalidateQueries({queryKey: ['businesses']})
      queryClient.invalidateQueries({queryKey: ['mi-area', businessId]})
      queryClient.invalidateQueries({queryKey: ['mi-area-personas', businessId]})
    },
  })
}

// Any member may call it; a non-responsable gets empty lists.
export function useMiArea(businessId: string | undefined) {
  return useQuery({
    queryKey: ['mi-area', businessId],
    queryFn: async () => (await api.get<MiArea>(`/businesses/${businessId}/mi-area`)).data,
    enabled: !!businessId,
  })
}

/** R6: un área de producción tiene 40-80 personas. */
const MI_AREA_PAGE_SIZE = 20

export function useMiAreaPersonas(businessId: string | undefined, page: number) {
  return usePagedList<MiAreaPersona>(
    ['mi-area-personas', businessId],
    businessId ? `/businesses/${businessId}/mi-area/personas` : undefined,
    {page, search: ''},
    MI_AREA_PAGE_SIZE
  )
}

// The only write a responsable de área may do.
export function useRenameMiArea(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({areaId, name}: {areaId: string; name: string}) =>
      (await api.patch(`/businesses/${businessId}/mi-area/areas/${areaId}`, {name})).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['mi-area', businessId]})
      queryClient.invalidateQueries({queryKey: ['mi-area-personas', businessId]})
      queryClient.invalidateQueries({queryKey: ['areas', businessId]})
      queryClient.invalidateQueries({queryKey: ['employees', businessId]})
    },
  })
}
